import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Phone, Mail, Instagram, MessageCircle, MapPin, CalendarDays, Check } from "lucide-react";
import { format } from "date-fns";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { contactContent as c } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  c.house.sanctuary.mapQuery
)}`;

const IconRow = ({ icon: Icon, label, href, external, testid }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noreferrer" : undefined}
    className="flex items-center gap-4 group"
    data-testid={testid}
  >
    <span className="w-12 h-12 shrink-0 border border-hairline-beige flex items-center justify-center text-brand-gold group-hover:border-brand-gold group-hover:bg-brand-gold/5 transition-colors duration-300">
      <Icon className="w-4 h-4" />
    </span>
    <span className="font-cormorant text-body-lg text-brand-ink group-hover:text-bright-gold-on-dark transition-colors">
      {label}
    </span>
  </a>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    occasion: "",
    date: "",
    guests: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [dateObj, setDateObj] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);

  const handleDateSelect = (d) => {
    setDateObj(d);
    setForm((f) => ({ ...f, date: d ? format(d, "d MMMM yyyy") : "" }));
    setDateOpen(false);
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        occasion: form.occasion,
        message: form.message,
        email: form.email,
        phone: form.phone,
        event_date: form.date,
        guests: form.guests,
      });
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again or write to us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const labelClass =
    "font-inter text-label-sm uppercase tracking-[0.2em] text-deep-taupe mb-2 block";
  const inputClass =
    "w-full bg-transparent border-0 border-b border-hairline-beige focus:border-brand-gold outline-none py-3 font-cormorant text-[18px] text-brand-ink placeholder:text-outline-variant transition-colors";

  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header />

      <main className="flex-grow pt-40 pb-narrative-gap px-margin-mobile md:px-margin-desktop">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter md:gap-16">
          {/* Left — invitation + correspondence */}
          <div className="col-span-1 md:col-span-5">
            <Reveal
              as="h1"
              className="font-cormorant text-[46px] md:text-[64px] leading-[1.0] text-brand-ink mb-6"
            >
              {c.eyebrow}
            </Reveal>
            <Reveal
              as="p"
              delay={100}
              className="font-cormorant text-[19px] md:text-[22px] leading-snug italic text-deep-taupe mb-10"
            >
              Every celebration ends.
              <br />
              The memories should not.
            </Reveal>
            <Reveal
              as="p"
              delay={200}
              className="font-cormorant text-body-md text-deep-taupe max-w-md mb-14"
            >
              {c.subtitle}
            </Reveal>

            <div className="flex flex-col gap-6">
              <Reveal>
                <IconRow
                  icon={Phone}
                  label={c.house.correspondence.phone}
                  href="tel:+919717093533"
                  testid="contact-phone-link"
                />
              </Reveal>
              <Reveal delay={80}>
                <IconRow
                  icon={Mail}
                  label={c.house.correspondence.email}
                  href={`mailto:${c.house.correspondence.email}`}
                  testid="contact-email-link"
                />
              </Reveal>
              <Reveal delay={160}>
                <IconRow
                  icon={MessageCircle}
                  label={c.house.correspondence.whatsapp}
                  href="https://wa.me/919717093533"
                  external
                  testid="contact-whatsapp-link"
                />
              </Reveal>
              <Reveal delay={240}>
                <IconRow
                  icon={Instagram}
                  label={c.house.correspondence.handle}
                  href="https://instagram.com/thekitchenartco"
                  external
                  testid="contact-instagram-link"
                />
              </Reveal>
              <Reveal delay={320}>
                <IconRow
                  icon={MapPin}
                  label={c.house.sanctuary.button}
                  href={mapsUrl}
                  external
                  testid="contact-map-link"
                />
              </Reveal>
            </div>
          </div>

          {/* Right — enquiry form */}
          <div className="col-span-1 md:col-span-6 md:col-start-7">
            {sent ? (
              <Reveal className="border border-brand-gold p-10 md:p-14 text-center flex flex-col items-center" data-testid="contact-success">
                <span className="tkac-seal-in w-16 h-16 rounded-full border border-brand-gold flex items-center justify-center mb-8 text-brand-gold">
                  <Check className="w-7 h-7 animate-fade-in" strokeWidth={1.5} />
                </span>
                <p className="font-cormorant text-[27px] text-brand-ink mb-3">
                  Thank you, {form.name || "friend"}.
                </p>
                <p className="font-cormorant text-body-md text-deep-taupe mb-4">
                  Your note has been received.
                </p>
                <p className="font-inter text-label-sm uppercase tracking-[0.2em] text-brand-secondary">
                  We will reply within 24 hours.
                </p>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={handleSubmit} className="flex flex-col gap-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    <div>
                      <label className={labelClass}>{c.fields.name}</label>
                      <input
                        required
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={c.placeholders.name}
                        className={inputClass}
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{c.fields.email}</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder={c.placeholders.email}
                        className={inputClass}
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{c.fields.phone}</label>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder={c.placeholders.phone}
                        className={inputClass}
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{c.fields.occasion}</label>
                      <select
                        required
                        name="occasion"
                        value={form.occasion}
                        onChange={handleChange}
                        className={`${inputClass} appearance-none cursor-pointer`}
                        data-testid="contact-occasion-select"
                      >
                        <option value="" disabled>
                          Select an occasion
                        </option>
                        {c.occasions.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>{c.fields.date}</label>
                      <Popover open={dateOpen} onOpenChange={setDateOpen}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className={`${inputClass} flex items-center justify-between text-left cursor-pointer ${
                              form.date ? "text-brand-ink" : "text-outline-variant"
                            }`}
                            data-testid="contact-date-trigger"
                          >
                            <span>{form.date || c.placeholders.date}</span>
                            <CalendarDays className="w-4 h-4 text-brand-gold shrink-0 ml-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 bg-background-ivory border border-hairline-beige"
                          align="start"
                          data-testid="contact-date-calendar"
                        >
                          <Calendar
                            mode="single"
                            selected={dateObj}
                            onSelect={handleDateSelect}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className={labelClass}>{c.fields.guests}</label>
                      <input
                        name="guests"
                        value={form.guests}
                        onChange={handleChange}
                        placeholder={c.placeholders.guests}
                        className={inputClass}
                        data-testid="contact-guests-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>{c.fields.message}</label>
                    <textarea
                      name="message"
                      rows={3}
                      value={form.message}
                      onChange={handleChange}
                      placeholder={c.placeholders.message}
                      className={`${inputClass} resize-none`}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="self-start inline-flex items-center gap-4 border border-brand-gold px-10 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500 disabled:opacity-60"
                    data-testid="contact-submit-button"
                  >
                    <span className="font-inter text-label-sm uppercase tracking-[0.25em]">
                      {submitting ? "Sending..." : c.button}
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  {error && (
                    <p className="font-inter text-label-sm text-error" data-testid="contact-error">
                      {error}
                    </p>
                  )}
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
